import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ExpenseService } from '../../services/expense.service';
import { ExpenseSummary } from '../../interfaces/expense.interface';
import { Chart, ChartConfiguration, ArcElement, Tooltip, Legend, DoughnutController } from 'chart.js';

Chart.register(ArcElement, Tooltip, Legend, DoughnutController);

@Component({
    selector: 'app-expense-summary',
    templateUrl: './expense-summary.component.html',
    styleUrls: ['./expense-summary.component.scss']
})
export class ExpenseSummaryComponent implements OnInit, OnDestroy {
    @ViewChild('pieChart') pieChartRef!: ElementRef<HTMLCanvasElement>;

    summary: ExpenseSummary | null = null;
    loading = false;
    error = '';

    collapsed: { [key: string]: boolean } = {
        fuel: false,
        tolls: false,
        accommodation: true,
        fines: true,
        food: true,
        other: true
    };

    private chart: Chart | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private expenseService: ExpenseService,
        private spinner: NgxSpinnerService
    ) {}

    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) this.loadSummary(parseInt(id));
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    loadSummary(serviceId: number): void {
        this.loading = true;
        this.spinner.show();
        this.expenseService.getExpenseSummary(serviceId).subscribe({
            next: (data) => {
                this.summary = data;
                this.loading = false;
                this.spinner.hide();
                setTimeout(() => this.buildChart(), 100);
            },
            error: () => {
                this.error = 'Error al cargar el resumen de gastos.';
                this.loading = false;
                this.spinner.hide();
            }
        });
    }

    toggle(section: string): void {
        this.collapsed[section] = !this.collapsed[section];
    }

    get balance(): number {
        if (!this.summary) return 0;
        return this.summary.initial_deposit - this.summary.total_expenses;
    }

    get balanceClass(): string {
        return this.balance >= 0 ? 'text-success' : 'text-danger';
    }

    formatCurrency(val: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);
    }

    buildChart(): void {
        if (!this.pieChartRef || !this.summary) return;
        this.chart?.destroy();
        const ctx = this.pieChartRef.nativeElement.getContext('2d');
        if (!ctx) return;

        const config: ChartConfiguration<'doughnut'> = {
            type: 'doughnut',
            data: {
                labels: ['Combustible', 'Casetas', 'Alojamiento', 'Multas', 'Alimentos', 'Otros'],
                datasets: [{
                    data: [
                        this.summary.total_fuel,
                        this.summary.total_tolls,
                        this.summary.total_accommodation,
                        this.summary.total_fines,
                        this.summary.total_food,
                        this.summary.total_other
                    ],
                    backgroundColor: ['#FF9800', '#0DB4E8', '#3F51B5', '#F44336', '#4CAF50', '#9C27B0'],
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                plugins: {
                    legend: { position: 'bottom' },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => ` ${this.formatCurrency(ctx.raw as number)}`
                        }
                    }
                }
            }
        };
        this.chart = new Chart(ctx, config);
    }

    downloadPdf(): void {
        if (!this.summary) return;
        this.expenseService.downloadSummaryPdf(this.summary.service_id).subscribe({
            next: (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `resumen-gastos-${this.summary?.folio}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
            },
            error: () => alert('Error al descargar el PDF.')
        });
    }

    goBack(): void {
        this.router.navigate(['/service/list']);
    }
}
